import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/common/Modal";
import { useAuth } from "@/providers/AuthProvider";
import {
  accountRepository,
  type ImportPreview,
  type ImportResult,
  type ManagedAccount,
} from "@/services/repositories/account-repository";

type AccountStatusFilter = "active" | "disabled" | "all";

export function AccountsPage(): JSX.Element {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<ManagedAccount[]>([]);
  const [status, setStatus] = useState<AccountStatusFilter>("active");
  const [page, setPage] = useState(1);
  const [listMeta, setListMeta] = useState({ total: 0, page: 1, pageSize: 20 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [revealed, setRevealed] = useState<{ username: string; password: string } | null>(null);
  const [disableTarget, setDisableTarget] = useState<ManagedAccount | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await accountRepository.list(status, page, 20);
      setAccounts(result.items);
      setListMeta({ total: result.total, page: result.page, pageSize: result.pageSize });
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "账号列表加载失败");
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  const totalPages = Math.max(1, Math.ceil(listMeta.total / listMeta.pageSize));

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="min-h-full p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-6">
        <section className="overflow-hidden rounded-3xl border border-brand/15 bg-white shadow-[0_22px_65px_-45px_rgba(51,65,85,0.6)]">
          <div className="flex flex-col gap-5 border-b border-slate-200 bg-[linear-gradient(125deg,rgba(77,125,40,0.09),rgba(255,255,255,0.96)_55%,rgba(196,82,24,0.07))] p-6 md:flex-row md:items-center md:justify-between md:p-8">
            <div>
              <p className="text-sm font-semibold tracking-wide text-brand">系统管理</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-900 md:text-3xl">账号管理</h1>
              <p className="mt-2 text-sm text-slate-500">
                创建独立学校教师账号，查看登录信息并停用账号。
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => setImportOpen(true)} className={secondaryButton}>
                批量导入
              </button>
              <button type="button" onClick={() => setCreateOpen(true)} className={primaryButton}>
                + 创建账号
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between md:px-8">
            <div className="flex gap-2" aria-label="账号状态筛选">
              {(
                [
                  ["active", "使用中"],
                  ["disabled", "已停用"],
                  ["all", "全部"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={status === value}
                  onClick={() => {
                    setStatus(value);
                    setPage(1);
                  }}
                  className={status === value ? primaryButton : secondaryButton}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-500">
              共 {listMeta.total} 个账号 · 第 {listMeta.page}/{totalPages} 页
            </p>
          </div>

          {error ? (
            <p
              role="alert"
              className="mx-5 mb-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 md:mx-8"
            >
              {error}
            </p>
          ) : null}
          {loading ? (
            <p className="p-10 text-center text-sm text-slate-500">正在加载账号…</p>
          ) : (
            <div className="overflow-x-auto px-5 pb-7 md:px-8">
              <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left text-sm">
                <thead>
                  <tr className="text-slate-500">
                    {["姓名", "登录账号", "所属学校", "状态", "创建时间", "操作"].map((label) => (
                      <th key={label} className="border-b border-slate-200 px-4 py-3 font-medium">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id} className="text-slate-700 hover:bg-slate-50/80">
                      <td className="border-b border-slate-100 px-4 py-4 font-semibold text-slate-900">
                        {account.displayName}
                        {account.isSuperAdmin ? (
                          <span className="ml-2 rounded-full bg-brand/10 px-2 py-1 text-xs text-brand-dark">
                            超级管理员
                          </span>
                        ) : null}
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4">{account.username}</td>
                      <td className="border-b border-slate-100 px-4 py-4">{account.tenant.name}</td>
                      <td className="border-b border-slate-100 px-4 py-4">
                        <StatusBadge status={account.status} />
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4">
                        {new Date(account.createdAt).toLocaleDateString("zh-CN")}
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4">
                        <div className="flex gap-2">
                          {account.hasManagedPassword ? (
                            <button
                              type="button"
                              onClick={async () => {
                                setError(null);
                                try {
                                  setRevealed(await accountRepository.reveal(account.id));
                                } catch (revealError) {
                                  setError(
                                    revealError instanceof Error
                                      ? revealError.message
                                      : "无法查看密码",
                                  );
                                }
                              }}
                              className={smallButton}
                            >
                              查看账号密码
                            </button>
                          ) : (
                            <span className="inline-flex min-h-10 items-center text-xs text-slate-400">
                              既有账号密码不可查看
                            </span>
                          )}
                          <button
                            type="button"
                            disabled={
                              account.id === user?.id ||
                              account.isSuperAdmin ||
                              account.status === "disabled"
                            }
                            onClick={() => setDisableTarget(account)}
                            className={`${smallButton} text-red-600 hover:border-red-300 disabled:text-slate-300`}
                          >
                            删除账号
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {accounts.length === 0 ? (
                <p className="py-12 text-center text-sm text-slate-500">当前筛选下没有账号</p>
              ) : null}
              {listMeta.total > listMeta.pageSize ? (
                <div className="mt-5 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    className={secondaryButton}
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    上一页
                  </button>
                  <button
                    type="button"
                    className={secondaryButton}
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  >
                    下一页
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </section>
      </div>

      <CreateAccountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          setCreateOpen(false);
          await load();
        }}
      />
      <ImportAccountsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />
      <Modal title="账号密码" open={Boolean(revealed)} onClose={() => setRevealed(null)}>
        {revealed ? (
          <div className="space-y-4">
            <CredentialRow label="登录账号" value={revealed.username} />
            <CredentialRow label="登录密码" value={revealed.password} />
            <p className="text-sm text-slate-500">
              请通过安全方式交付教师。关闭窗口后，本页不会保留密码。
            </p>
          </div>
        ) : null}
      </Modal>
      <Modal
        title="确认删除账号"
        open={Boolean(disableTarget)}
        onClose={() => setDisableTarget(null)}
      >
        {disableTarget ? (
          <div className="space-y-5">
            <p className="text-sm leading-6 text-slate-600">
              将停用{" "}
              <strong>
                {disableTarget.displayName}（{disableTarget.username}）
              </strong>{" "}
              的登录权限，学校、儿童、评估和报告数据都会保留。
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className={secondaryButton}
                onClick={() => setDisableTarget(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="inline-flex min-h-11 items-center rounded-xl bg-red-600 px-5 font-semibold text-white hover:bg-red-700"
                onClick={async () => {
                  try {
                    await accountRepository.disable(disableTarget.id);
                    setDisableTarget(null);
                    await load();
                  } catch (disableError) {
                    setError(disableError instanceof Error ? disableError.message : "停用失败");
                    setDisableTarget(null);
                  }
                }}
              >
                确认停用
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function CreateAccountModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void>;
}): JSX.Element {
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="创建教师账号"
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <form
        className="space-y-5"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await accountRepository.create({ displayName, phone });
            setDisplayName("");
            setPhone("");
            await onCreated();
          } catch (createError) {
            setError(createError instanceof Error ? createError.message : "创建失败");
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="block text-sm font-medium text-slate-700">
          姓名
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            maxLength={100}
            className={inputClass}
          />
        </label>
        <label className="block text-sm font-medium text-slate-700">
          手机号
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            inputMode="numeric"
            pattern="1[3-9][0-9]{9}"
            className={inputClass}
          />
        </label>
        {error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-3">
          <button type="button" disabled={busy} className={secondaryButton} onClick={onClose}>
            取消
          </button>
          <button type="submit" disabled={busy} className={primaryButton}>
            {busy ? "正在创建…" : "创建账号"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ImportAccountsModal({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => Promise<void>;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  function close() {
    if (!busy) {
      setFile(null);
      setPreview(null);
      setResult(null);
      setError(null);
      onClose();
    }
  }
  return (
    <Modal title="批量导入账号" open={open} onClose={close} widthClassName="max-w-2xl">
      <div className="space-y-5">
        <button
          type="button"
          className={secondaryButton}
          onClick={() => {
            void accountRepository
              .downloadTemplate()
              .catch((downloadError) =>
                setError(downloadError instanceof Error ? downloadError.message : "模板下载失败"),
              );
          }}
        >
          下载 Excel 模板
        </button>
        <label className="block text-sm font-medium text-slate-700">
          选择填写后的 XLSX 文件
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="mt-2 block w-full text-sm"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setPreview(null);
              setResult(null);
              setError(null);
            }}
          />
        </label>
        <button
          type="button"
          disabled={!file || busy}
          className={secondaryButton}
          onClick={async () => {
            if (!file) return;
            setBusy(true);
            setError(null);
            try {
              setPreview(await accountRepository.previewImport(file));
            } catch (previewError) {
              setError(previewError instanceof Error ? previewError.message : "文件预检失败");
            } finally {
              setBusy(false);
            }
          }}
        >
          预检文件
        </button>
        {preview ? (
          <div
            className={`rounded-xl border p-4 text-sm ${preview.valid ? "border-brand/20 bg-brand/5 text-brand-dark" : "border-red-200 bg-red-50 text-red-700"}`}
          >
            <p>
              {preview.valid
                ? `校验通过，共 ${preview.rowCount} 个账号`
                : "校验未通过，未创建任何账号"}
            </p>
            {preview.issues.length ? (
              <ul className="mt-2 list-disc pl-5">
                {preview.issues.map((issue, index) => (
                  <li key={`${issue.row}-${index}`}>
                    {issue.row > 0 ? `第 ${issue.row} 行：` : ""}
                    {issue.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {result ? (
          <div role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
            <p>
              导入完成：成功 {result.created}，失败 {result.failed}，结果未确认 {result.unconfirmed}
              ，未执行 {result.notProcessed}。
            </p>
            <ul className="mt-3 space-y-2">
              {result.rows.map((row) => (
                <li
                  key={row.row}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2"
                >
                  <span>
                    第 {row.row} 行 · {importStatusLabel(row.status)}
                  </span>
                  <span className="text-slate-500">
                    {row.account?.username ?? row.message ?? "—"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-3">
          <button type="button" disabled={busy} className={secondaryButton} onClick={close}>
            关闭
          </button>
          <button
            type="button"
            disabled={!file || !preview?.valid || busy || Boolean(result)}
            className={primaryButton}
            onClick={async () => {
              if (!file || !preview) return;
              setBusy(true);
              setError(null);
              try {
                setResult(await accountRepository.applyImport(file, preview.fileSha256));
                await onImported();
              } catch (applyError) {
                const message = applyError instanceof Error ? applyError.message : "导入失败";
                setError(`${message}；若请求曾中断，请先刷新账号列表核实，勿直接重复提交。`);
              } finally {
                setBusy(false);
              }
            }}
          >
            确认导入
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StatusBadge({ status }: { status: ManagedAccount["status"] }): JSX.Element {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
    >
      {status === "active" ? "使用中" : "已停用"}
    </span>
  );
}
function importStatusLabel(status: ImportResult["rows"][number]["status"]): string {
  if (status === "created") return "已创建";
  if (status === "unconfirmed") return "结果未确认";
  if (status === "not_processed") return "未执行";
  return "失败";
}
function CredentialRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 break-all font-mono text-base font-semibold text-slate-900">{value}</p>
    </div>
  );
}

const primaryButton =
  "inline-flex min-h-11 items-center justify-center rounded-xl bg-brand px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300";
const secondaryButton =
  "inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:border-brand/40 hover:text-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-slate-300";
const smallButton =
  "inline-flex min-h-10 items-center rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:border-brand/40 hover:text-brand-dark disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-300";
const inputClass =
  "mt-2 min-h-11 w-full rounded-xl border border-slate-300 px-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";
